import { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { api, type ProductListResponse } from '../lib/api';
import { useStore } from '../lib/store';
import { usePageMeta } from '../lib/meta';
import { price } from '../lib/format';
import { ProductGrid } from '../components/ProductCard';
import {
  Button,
  EmptyState,
  PageHeading,
  ProductGridSkeleton,
} from '../components/ui';

const SORT_OPTIONS = [
  ['featured', 'Featured'],
  ['newest', 'Newest first'],
  ['price_asc', 'Price: low to high'],
  ['price_desc', 'Price: high to low'],
  ['title_asc', 'Name: A–Z'],
] as const;

/**
 * Filter state lives in the URL, not component state.
 *
 * That makes a filtered view shareable and bookmarkable, and makes the browser
 * back button behave the way shoppers expect after drilling into a filter.
 */
function useFilters() {
  const [params, setParams] = useSearchParams();

  const filters = useMemo(
    () => ({
      tags: params.get('tags')?.split(',').filter(Boolean) ?? [],
      sort: params.get('sort') ?? 'featured',
      minPrice: params.get('minPrice') ?? '',
      maxPrice: params.get('maxPrice') ?? '',
      onSale: params.get('onSale') === 'true',
      inStock: params.get('inStock') === 'true',
      page: Number(params.get('page') ?? '1'),
      q: params.get('q') ?? '',
    }),
    [params],
  );

  const update = useCallback(
    (patch: Record<string, string | string[] | boolean | number | null>) => {
      const next = new URLSearchParams(params);
      for (const [key, value] of Object.entries(patch)) {
        const empty =
          value === null ||
          value === '' ||
          value === false ||
          (Array.isArray(value) && value.length === 0);
        if (empty) next.delete(key);
        else next.set(key, Array.isArray(value) ? value.join(',') : String(value));
      }
      // Any filter change invalidates the current page number.
      if (!('page' in patch)) next.delete('page');
      setParams(next, { replace: false });
    },
    [params, setParams],
  );

  const clearAll = useCallback(() => {
    const next = new URLSearchParams();
    const q = params.get('q');
    if (q) next.set('q', q);
    setParams(next);
  }, [params, setParams]);

  return { filters, update, clearAll };
}

function FilterPanel({
  facets,
  filters,
  update,
  clearAll,
  currency,
}: {
  facets: ProductListResponse['facets'];
  filters: ReturnType<typeof useFilters>['filters'];
  update: ReturnType<typeof useFilters>['update'];
  clearAll: () => void;
  currency: string;
}) {
  const activeCount =
    filters.tags.length +
    (filters.onSale ? 1 : 0) +
    (filters.inStock ? 1 : 0) +
    (filters.minPrice ? 1 : 0) +
    (filters.maxPrice ? 1 : 0);

  const toggleTag = (tag: string) => {
    update({
      tags: filters.tags.includes(tag)
        ? filters.tags.filter((t) => t !== tag)
        : [...filters.tags, tag],
    });
  };

  return (
    <aside className="space-y-8">
      <div className="flex items-center justify-between">
        <h2 className="text-xs uppercase tracking-widest">Filter</h2>
        {activeCount > 0 && (
          <button
            type="button"
            onClick={clearAll}
            className="cursor-pointer text-xs text-muted underline underline-offset-2 hover:text-ink"
          >
            Clear ({activeCount})
          </button>
        )}
      </div>

      <fieldset>
        <legend className="mb-3 text-xs uppercase tracking-widest text-muted">
          Availability
        </legend>
        <label className="flex cursor-pointer items-center gap-2 py-1 text-sm">
          <input
            type="checkbox"
            checked={filters.inStock}
            onChange={(event) => update({ inStock: event.target.checked })}
            className="accent-[var(--color-primary)]"
          />
          In stock ({facets.inStockCount})
        </label>
        <label className="flex cursor-pointer items-center gap-2 py-1 text-sm">
          <input
            type="checkbox"
            checked={filters.onSale}
            onChange={(event) => update({ onSale: event.target.checked })}
            className="accent-[var(--color-primary)]"
          />
          On sale ({facets.onSaleCount})
        </label>
      </fieldset>

      <fieldset>
        <legend className="mb-3 text-xs uppercase tracking-widest text-muted">Price</legend>
        <p className="mb-2 text-xs text-muted">
          {price(facets.priceRange.minMinor, currency as 'GBP')} –{' '}
          {price(facets.priceRange.maxMinor, currency as 'GBP')}
        </p>
        <div className="flex items-center gap-2">
          <input
            type="number"
            inputMode="numeric"
            placeholder="Min £"
            aria-label="Minimum price in pounds"
            defaultValue={filters.minPrice ? Number(filters.minPrice) / 100 : ''}
            onBlur={(event) => {
              const value = event.target.value;
              update({ minPrice: value ? Math.round(Number(value) * 100) : null });
            }}
            className="w-full border border-border bg-transparent px-2 py-1.5 text-xs outline-none focus:border-ink"
          />
          <span className="text-muted">–</span>
          <input
            type="number"
            inputMode="numeric"
            placeholder="Max £"
            aria-label="Maximum price in pounds"
            defaultValue={filters.maxPrice ? Number(filters.maxPrice) / 100 : ''}
            onBlur={(event) => {
              const value = event.target.value;
              update({ maxPrice: value ? Math.round(Number(value) * 100) : null });
            }}
            className="w-full border border-border bg-transparent px-2 py-1.5 text-xs outline-none focus:border-ink"
          />
        </div>
      </fieldset>

      {facets.tags.length > 0 && (
        <fieldset>
          <legend className="mb-3 text-xs uppercase tracking-widest text-muted">Style</legend>
          <div className="max-h-80 space-y-1 overflow-y-auto pr-1">
            {facets.tags.slice(0, 40).map((tag) => (
              <label
                key={tag.value}
                className="flex cursor-pointer items-center gap-2 py-0.5 text-sm"
              >
                <input
                  type="checkbox"
                  checked={filters.tags.includes(tag.value)}
                  onChange={() => toggleTag(tag.value)}
                  className="accent-[var(--color-primary)]"
                />
                <span className="flex-1 leading-snug">{tag.label}</span>
                <span className="text-xs text-muted">{tag.count}</span>
              </label>
            ))}
          </div>
        </fieldset>
      )}
    </aside>
  );
}

export function CollectionPage({ searchMode = false }: { searchMode?: boolean }) {
  const { slug } = useParams<{ slug: string }>();
  const { categoryBySlug, data: storeData } = useStore();
  const { filters, update, clearAll } = useFilters();

  const [result, setResult] = useState<ProductListResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);

  const category = slug ? categoryBySlug(slug) : undefined;
  const heading = searchMode
    ? filters.q
      ? `Search: “${filters.q}”`
      : 'Search'
    : (category?.title ?? 'All jewellery');

  usePageMeta({
    title: `${heading} | ${storeData?.store?.name ?? "Gill's Handmade"}`,
    description: category?.description ?? '',
  });

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    void api
      .products({
        category: searchMode ? undefined : slug,
        q: filters.q || undefined,
        tags: filters.tags,
        sort: filters.sort,
        minPrice: filters.minPrice || undefined,
        maxPrice: filters.maxPrice || undefined,
        onSale: filters.onSale,
        inStock: filters.inStock,
        page: filters.page,
        pageSize: 24,
      })
      .then((response) => {
        if (!cancelled) setResult(response);
      })
      .catch(() => {
        if (!cancelled) setError('We could not load these pieces. Please try again.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [slug, searchMode, filters]);

  // Scroll back to the top when paging, otherwise page 2 opens mid-grid.
  useEffect(() => {
    if (filters.page > 1) window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [filters.page]);

  return (
    <div className="page-container py-10">
      <PageHeading
        title={heading}
        description={searchMode ? undefined : category?.description}
        count={result?.total}
      />

      <div className="mt-8 grid gap-10 lg:grid-cols-[240px_1fr]">
        <div className="hidden lg:block">
          {result && (
            <FilterPanel
              facets={result.facets}
              filters={filters}
              update={update}
              clearAll={clearAll}
              currency={storeData?.store?.currency ?? 'GBP'}
            />
          )}
        </div>

        <div>
          <div className="mb-6 flex items-center justify-between gap-4">
            <Button
              variant="secondary"
              size="sm"
              className="lg:hidden"
              onClick={() => setMobileFiltersOpen((v) => !v)}
            >
              {mobileFiltersOpen ? 'Hide filters' : 'Filters'}
            </Button>

            <label className="ml-auto flex items-center gap-2 text-xs uppercase tracking-widest text-muted">
              Sort
              <select
                value={filters.sort}
                onChange={(event) => update({ sort: event.target.value })}
                className="cursor-pointer border border-border bg-transparent px-2 py-1.5 text-xs outline-none focus:border-ink"
              >
                {SORT_OPTIONS.map(([value, label]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </select>
            </label>
          </div>

          {mobileFiltersOpen && result && (
            <div className="mb-8 border border-border p-5 lg:hidden">
              <FilterPanel
                facets={result.facets}
                filters={filters}
                update={update}
                clearAll={clearAll}
                currency={storeData?.store?.currency ?? 'GBP'}
              />
            </div>
          )}

          {loading && !result ? (
            <ProductGridSkeleton count={12} />
          ) : error ? (
            <EmptyState title="Something went wrong" message={error} />
          ) : result && result.products.length === 0 ? (
            <EmptyState
              title="Nothing matches those filters"
              message="Try removing a filter or widening the price range."
              action={
                <Button variant="secondary" onClick={clearAll}>
                  Clear filters
                </Button>
              }
            />
          ) : (
            result && (
              <>
                <div className={loading ? 'opacity-50 transition-opacity' : ''}>
                  <ProductGrid products={result.products} eagerCount={4} />
                </div>

                {result.pageCount > 1 && (
                  <nav
                    className="mt-14 flex items-center justify-center gap-3"
                    aria-label="Pagination"
                  >
                    <Button
                      variant="secondary"
                      size="sm"
                      disabled={result.page <= 1}
                      onClick={() => update({ page: result.page - 1 })}
                    >
                      Previous
                    </Button>
                    <span className="text-xs uppercase tracking-widest text-muted">
                      Page {result.page} of {result.pageCount}
                    </span>
                    <Button
                      variant="secondary"
                      size="sm"
                      disabled={result.page >= result.pageCount}
                      onClick={() => update({ page: result.page + 1 })}
                    >
                      Next
                    </Button>
                  </nav>
                )}
              </>
            )
          )}
        </div>
      </div>
    </div>
  );
}
